import { Box, Button, Typography, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import axios from 'axios';
import { Tooltip } from '@mui/material';
import { AuthContext } from '../../context/AuthContext';
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

const Categories = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [categories, setCategories] = useState(null);
  const { currentuser, base_url } = useContext(AuthContext);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryType, setCategoryType] = useState('Product'); // Default value
  const [editCategoryId, setEditCategoryId] = useState(null);

  const config = {
    headers: {
      'Authorization': `Bearer ${currentuser.accessToken}`
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return date.toLocaleDateString(undefined, options); // Format as MM/DD/YYYY
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.post(`${base_url}/ethioglobal/categories/all`, config);
      setCategories(response.data);
    } catch (error) {}
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddCategory = async () => {
    try {
      await axios.post(`${base_url}/ethioglobal/categories/add`, { 
        categoryname: categoryName, 
        categorytype: categoryType // Send category type in the request
      }, config);
      setCategoryName('');
      setCategoryType('Product'); // Reset to default
      setOpenAddModal(false);
      fetchCategories();
    } catch (error) {}
  };

  const handleEditCategory = async () => {
    try {
      await axios.put(`${base_url}/ethioglobal/categories/edit/${editCategoryId}`, { 
        name: categoryName, 
        type: categoryType // Include category type for editing
      }, config);
      setCategoryName('');
      setCategoryType('Product'); // Reset to default
      setOpenEditModal(false);
      fetchCategories();
    } catch (error) {}
  };

  const columns = [
    {
      field: "categoryname",
      headerName: "Category Name",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "categorytype",
      headerName: "Category Type",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      flex: 1,
      hide: isSmallScreen,
      valueFormatter: ({ value }) => {
        const date = new Date(value);
        return date.toLocaleString(); // Customize this as needed
      },
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span>{new Date(params.value).toLocaleString()}</span>
        </Tooltip>
      ),
    },
    {
      field: "id",
      headerName: "",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="Edit" arrow>
          <span
            style={{
              cursor: 'pointer',
              display: 'flex',
              marginRight: 10,
              alignItems: 'center',
              justifyContent: 'flex-end',
              height: '100%',
              width: '100%',
            }}
            onClick={() => {
              setEditCategoryId(params.value);
              setCategoryName(params.row.categoryname);
              setCategoryType(params.row.categorytype); // Set the type for editing
              setOpenEditModal(true);
            }}
          >
            <EditIcon />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header subtitle="Categories List" />
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => setOpenAddModal(true)}
        >
          Add Category
        </Button>
      </Box>
      <Box
        m="0px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        {categories !== null ? 
          <DataGrid 
            rows={categories} 
            columns={columns} 
            pageSize={5} 
            rowsPerPageOptions={[5]} 
            sx={{
              '& .overflow-cell': overflowCellStyle,
            }}
          />
          :
          <Typography>No rows to show.</Typography>
        }
      </Box>

      {/* Add Category Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            type="text"
            fullWidth
            variant="outlined"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Category Type</InputLabel>
            <Select
              value={categoryType}
              onChange={(e) => setCategoryType(e.target.value)}
              label="Category Type"
            >
              <MenuItem value="Product">Product</MenuItem>
              <MenuItem value="Service">Service</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)}>Cancel</Button>
          <Button onClick={handleAddCategory}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            type="text"
            fullWidth
            variant="outlined"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Category Type</InputLabel>
            <Select
              value={categoryType}
              onChange={(e) => setCategoryType(e.target.value)}
              label="Category Type"
            >
              <MenuItem value="Product">Product</MenuItem>
              <MenuItem value="Service">Service</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
          <Button onClick={handleEditCategory}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const overflowCellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default Categories;